import { SortOrder } from 'antd/es/table/interface'
import { ColumnsType } from 'antd/es/table'
import { Sorter } from '../../components/Sorter/Sorter'
import { Checkbox } from '../../components/Checkbox'
import { ActionsCell } from './ActionsCell/ActionsCell'
import { StatusWithDateCell } from '../../components/Table/Cells/StatusWithDateCell/StatusWithDateCell'
import { Button, BUTTON_TYPES } from '../../components/Button'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { ROLES } from '../Permission'
import { STATUS_TYPES_CLASSNAMES } from '../../components/Status'
import { ReactComponent as Information } from 'assets/svg/Information.svg'
import {
  ReassignmentStatuses,
  ReassignmentTypes,
  UserContractCategoriesColumnsFunc,
  UserContractCategory
} from './types'

export const REASSIGNMENT_STATUS_TO_CLASSNAME = {
  [ReassignmentStatuses.PENDING]: STATUS_TYPES_CLASSNAMES.YELLOW
} as const

export const REASSIGNMENT_STATUS_NAME = {
  [ReassignmentStatuses.PENDING]: 'Pending'
} as const

export const ASSIGNMENT_TYPE_NAME = {
  [ReassignmentTypes.REASSIGNMENT]: 'reassignment',
  [ReassignmentTypes.UNASSIGNMENT]: 'removal'
} as const

export const columns: UserContractCategoriesColumnsFunc = ({
  role,
  onChangeCommunityLevel,
  setSelectedCC,
  onDeleteCC,
  canManageContractCategories,
  communityLevelCheckboxesVisible,
  communityLevelCheckboxesEditable,
  categoryAssignmentVisible,
  userRole,
  hideActions,
  extendedViewModeOnly,
  simpleViewModeOnly
}) => {
  const nameWidth =
    !(canManageContractCategories || extendedViewModeOnly) &&
    !(!extendedViewModeOnly && !hideActions && !simpleViewModeOnly) // if only 1 or 2 columns are available
      ? 150
      : undefined
  const mainColumns: ColumnsType<UserContractCategory> = [
    {
      dataIndex: 'name',
      key: 'name',
      className: 'vertical-align-middle',
      width: role === ROLES.CONTRACT_STEWARD ? 380 : nameWidth,
      title: ({ sortOrder, sortColumn }) => (
        <Sorter
          dataIndex="name"
          sortColumn={sortColumn}
          sortOrder={sortOrder as SortOrder}
        >
          Contract category
        </Sorter>
      ),
      sorter: true,
      showSorterTooltip: false
    }
  ]
  if (communityLevelCheckboxesVisible) {
    mainColumns.push({
      dataIndex: 'community_level',
      key: 'community_level',
      className: 'vertical-align-middle',
      width: canManageContractCategories ? 156 : 300,
      title: () => (
        <div className="row align-center gap-4">
          Community level
          <Tooltip title="Enable this option if the user should have permissions to create RFP, contracts, pipelines at the community level.">
            <Button.Icon
              icon={<Information />}
              type={BUTTON_TYPES.GHOST}
              small
            />
          </Tooltip>
        </div>
      ),
      render: (
        _,
        { uuid, name, community_level, community_level_disabled }
      ) => (
        <Checkbox
          propsCheckbox={{
            disabled:
              extendedViewModeOnly ||
              !communityLevelCheckboxesEditable ||
              community_level_disabled,
            checked: !!community_level,
            onChange: () =>
              onChangeCommunityLevel?.(uuid, name, !community_level)
          }}
        />
      )
    })
  }
  if (canManageContractCategories || extendedViewModeOnly) {
    mainColumns.push({
      dataIndex: 'reassignment_request',
      key: 'reassignment_request',
      className: 'vertical-align-middle',
      title: 'Assignment status',
      width: 640,
      render: (_, { reassignment_request }) =>
        reassignment_request?.status ? (
          <StatusWithDateCell
            statusName={REASSIGNMENT_STATUS_NAME[reassignment_request.status]}
            statusClassName={
              REASSIGNMENT_STATUS_TO_CLASSNAME[reassignment_request.status]
            }
            dateLabel={`Date of ${
              ASSIGNMENT_TYPE_NAME[reassignment_request.type]
            }`}
            date={reassignment_request.effective_date}
          />
        ) : (
          '-'
        )
    })
  }

  if (!extendedViewModeOnly && !hideActions && !simpleViewModeOnly) {
    mainColumns.push({
      dataIndex: 'actions',
      key: 'actions',
      className: 'vertical-align-middle',
      title: '',
      width: 24,
      fixed: 'right',
      render: (_, cc) => {
        const { community_level, reassignment_request } = cc
        if (userRole === ROLES.COMMUNITY_PRESIDENT && !community_level) {
          return null
        }
        const canPresidentHandleAssignment =
          userRole === ROLES.COMMUNITY_PRESIDENT &&
          !!community_level &&
          categoryAssignmentVisible &&
          reassignment_request?.type !== ReassignmentTypes.UNASSIGNMENT

        const canDelete =
          userRole === ROLES.COMMUNITY_PRESIDENT &&
          !!community_level &&
          !reassignment_request?.type &&
          !canManageContractCategories

        const canManageAssignment =
          (userRole === ROLES.VICE_PRESIDENT && categoryAssignmentVisible) ||
          userRole === ROLES.COMMONS_VICE_PRESIDENT ||
          canPresidentHandleAssignment

        if (canManageAssignment || canDelete) {
          return (
            <ActionsCell
              cc={cc}
              setSelectedCC={setSelectedCC}
              onDeleteCC={onDeleteCC}
              canDelete={canDelete}
              canManageAssignment={canManageAssignment}
              isReview={
                userRole === ROLES.COMMUNITY_PRESIDENT &&
                !!reassignment_request?.type
              }
            />
          )
        }
      }
    })
  }
  return mainColumns
}
